// skipcq: JS-W1028
import { useEffect, useState, useCallback } from 'react';
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  setDoc,
  getDoc,
  deleteDoc,
  deleteField,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { SPLAT_DEFINITIONS_COLLECTION } from '../utils/firestoreCollections';
import {
  validateSplatType,
  normalizeCompletionSplatIds,
} from '../domain/splatConstants';

/**
 * useSplatsList
 * Fetches all documents from the `splat_definitions` collection.
 */
function useSplatsList() {
  const [splats, setSplats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const splatsRef = collection(db, SPLAT_DEFINITIONS_COLLECTION);
      const splatsQuery = query(splatsRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(splatsQuery);
      const rows = snapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }));
      setSplats(rows);
    } catch {
      setError('Failed to load splat definitions');
      // TODO: capture error to Sentry
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * Update a single field on a splat document and optimistically update local state.
   * Numeric fields are coerced to numbers.
   */
  const updateSplatField = useCallback(async (id, field, value) => {
    try {
      // Coerce specific numeric fields
      const numericFields = new Set(['limit', 'triggerThreshold']);
      const booleanFields = new Set(['isActive']);
      let coercedValue = value;
      if (field === 'type') {
        const normalized = validateSplatType(value);
        if (normalized === null) {
          throw new Error('Invalid splat type');
        }
        coercedValue = normalized;
      } else if (numericFields.has(field)) {
        coercedValue = Number(value || 0);
      } else if (booleanFields.has(field)) {
        coercedValue = Boolean(value);
      }

      const ref = doc(db, SPLAT_DEFINITIONS_COLLECTION, id);
      await updateDoc(ref, { [field]: coercedValue });

      // Optimistically update local state
      setSplats((prev) =>
        prev.map((s) => (s.id === id ? { ...s, [field]: coercedValue } : s)),
      );
    } catch (err) {
      setError('Failed to update splat definition');
      // TODO: capture error to Sentry
      throw err;
    }
  }, []);

  /**
   * Replace splat fields (used by the edit dialog).
   */
  const updateSplat = useCallback(async (id, data) => {
    const beerJourneyMapTrimmed = (data.beerJourneyMap || '').trim();
    const overlayTrimmed = (data.overlayText || '').trim();
    const thRaw = data.triggerThreshold;
    const hasThreshold =
      thRaw !== '' && thRaw !== null && thRaw !== undefined;

    const normalizedType = validateSplatType(data.type);
    if (normalizedType === null) {
      throw new Error('Invalid splat type');
    }

    const updates = {
      name: (data.name || '').trim(),
      text: (data.text || '').trim(),
      image: (data.image || '').trim(),
      limit: Number(data.limit || 0),
      isActive: Boolean(data.isActive ?? true),
      type: normalizedType,
      updatedAt: serverTimestamp(),
    };

    if (overlayTrimmed) {
      updates.overlayText = overlayTrimmed;
    } else {
      updates.overlayText = deleteField();
    }

    if (beerJourneyMapTrimmed) {
      updates.beerJourneyMap = beerJourneyMapTrimmed;
    } else {
      updates.beerJourneyMap = deleteField();
    }

    const typeUsesThreshold =
      normalizedType !== 'completion' &&
      normalizedType !== 'first_event_review';

    if (hasThreshold && typeUsesThreshold) {
      updates.triggerThreshold = Number(thRaw);
    } else {
      updates.triggerThreshold = deleteField();
    }

    if (normalizedType === 'completion') {
      updates.completionSplatIds = normalizeCompletionSplatIds(
        data.completionSplatIds,
      );
    } else {
      updates.completionSplatIds = deleteField();
    }

    if (normalizedType === 'first_event_review') {
      const eventIdTrimmed = String(data.eventId ?? '').trim();
      if (eventIdTrimmed) {
        updates.eventId = eventIdTrimmed;
      } else {
        updates.eventId = deleteField();
      }
    } else {
      updates.eventId = deleteField();
    }

    try {
      const ref = doc(db, SPLAT_DEFINITIONS_COLLECTION, id);
      await updateDoc(ref, updates);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        throw new Error('Splat missing after update');
      }
      const fresh = { id, ...snap.data() };
      setSplats((prev) => prev.map((s) => (s.id === id ? fresh : s)));
    } catch (err) {
      setError('Failed to update splat definition');
      throw err;
    }
  }, []);

  /**
   * Remove a splat definition document.
   */
  const deleteSplat = useCallback(async (id) => {
    try {
      await deleteDoc(doc(db, SPLAT_DEFINITIONS_COLLECTION, id));
      setSplats((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setError('Failed to delete splat definition');
      throw err;
    }
  }, []);

  /**
   * Create a new splat document and optimistically add to local state.
   */
  const createSplat = useCallback(
    async ({
      id,
      name,
      text,
      image,
      limit,
      isActive,
      type,
      overlayText,
      beerJourneyMap,
      triggerThreshold,
      completionSplatIds,
      eventId,
    }) => {
      const normalizedType = validateSplatType(type);
      if (normalizedType === null) {
        throw new Error('Invalid splat type');
      }

      const payload = {
        name: (name || '').trim(),
        text: (text || '').trim(),
        image: (image || '').trim(),
        limit: Number(limit || 0),
        isActive: Boolean(isActive ?? true),
        type: normalizedType,
        updatedAt: serverTimestamp(),
      };

      if (normalizedType === 'completion') {
        payload.completionSplatIds = normalizeCompletionSplatIds(
          completionSplatIds,
        );
      }

      if (normalizedType === 'first_event_review') {
        const eventIdTrimmed = String(eventId ?? '').trim();
        if (eventIdTrimmed) {
          payload.eventId = eventIdTrimmed;
        }
      }

      const overlayTrimmed = (overlayText || '').trim();
      if (overlayTrimmed) {
        payload.overlayText = overlayTrimmed;
      }

      const beerJourneyMapTrimmed = (beerJourneyMap || '').trim();
      if (beerJourneyMapTrimmed) {
        payload.beerJourneyMap = beerJourneyMapTrimmed;
      }

      const thRaw = triggerThreshold;
      if (
        thRaw !== '' &&
        thRaw !== null &&
        thRaw !== undefined &&
        normalizedType !== 'completion' &&
        normalizedType !== 'first_event_review'
      ) {
        payload.triggerThreshold = Number(thRaw);
      }

      try {
        const collRef = collection(db, SPLAT_DEFINITIONS_COLLECTION);
        let createdId = '';
        let createdRef = '';
        const cleanId = id?.trim();
        if (cleanId) {
          const ref = doc(collRef, cleanId);
          const existing = await getDoc(ref);
          if (existing.exists()) {
            throw new Error(
              'Document ID already exists. Choose a different ID.',
            );
          }
          await setDoc(ref, payload);
          createdId = cleanId;
          createdRef = ref;
        } else {
          const addedRef = await addDoc(collRef, payload);
          createdId = addedRef.id;
          createdRef = addedRef;
        }

        // Re-read the created document to get server-resolved fields (e.g., timestamps)
        const freshSnap = await getDoc(createdRef);
        const freshData = freshSnap.exists()
          ? { id: createdId, ...freshSnap.data() }
          : { id: createdId, ...payload };

        // Insert (or replace optimistic if present) with server-resolved data
        setSplats((prev) => {
          const hadOptimistic = prev.some((s) => s.id === createdId);
          if (hadOptimistic) {
            return prev.map((s) => (s.id === createdId ? freshData : s));
          }
          return [freshData, ...prev];
        });

        return createdId;
      } catch (err) {
        setError('Failed to create splat definition');
        // TODO: capture error to Sentry
        throw err;
      }
    },
    [],
  );

  return {
    splats,
    loading,
    error,
    refresh,
    updateSplatField,
    updateSplat,
    deleteSplat,
    createSplat,
  };
}

export default useSplatsList;
